import type {
  BoxListItem,
  BoxProductItem,
  IWarehouseApi,
  ProductJournalEntry,
} from "../../../api/WarehouseApi";
import {
  getBoxHistoryProductJournalActionTitle,
  getBoxHistoryProductJournalActionTone,
} from "../../shared/use-cases/journal-action-presentation";

export interface BoxHistoryItemView {
  id: string;
  title: string;
  tone: "info" | "warning" | "danger";
  timestamp: string;
  actorType: "user" | "system";
  actorLabel: string;
  productId: string;
  productLabel: string;
  productHint: string;
  boxId: string;
  quantityLabel: string;
  boxLabel: string;
  boxHint: string;
  comment: string | null;
}

interface BuildHistoryItemsInput {
  box: { id: string; name: string };
  entries: ProductJournalEntry[];
  childBoxes: BoxListItem[];
  childProducts: BoxProductItem[];
  historyProductNamesById: Record<string, string>;
}

function formatDate(date: ProductJournalEntry["date"]): string {
  if (!date) return "дата неизвестна";

  const day = String(date.day).padStart(2, "0");
  const month = String(date.month).padStart(2, "0");
  const year = String(date.year);
  const hour = String(date.hour).padStart(2, "0");
  const minute = String(date.minute).padStart(2, "0");

  return `${day}.${month}.${year} ${hour}:${minute}`;
}

function formatQuantity(value: number): string {
  if (!Number.isFinite(value)) {
    return "1";
  }

  const normalized = Math.max(0, value);
  return new Intl.NumberFormat("ru-RU", {
    maximumFractionDigits: 3,
  }).format(normalized);
}

function shortenId(value: string): string {
  const id = value.trim();
  if (!id) {
    return "—";
  }

  if (id.length <= 18) {
    return id;
  }

  return `${id.slice(0, 12)}...${id.slice(-4)}`;
}

function resolveHistoryProductLabel(input: {
  productId: string;
  historyProductNamesById: Record<string, string>;
  childProducts: BoxProductItem[];
}): string {
  const trimmedId = input.productId.trim();
  const fromMap = input.historyProductNamesById[trimmedId];
  if (fromMap) {
    return fromMap;
  }

  const fromContents = input.childProducts.find(
    (item) => item.id === trimmedId,
  );
  if (fromContents?.name?.trim()) {
    return fromContents.name.trim();
  }

  if (!trimmedId) {
    return "Неизвестный товар";
  }

  return `Товар ${shortenId(trimmedId)}`;
}

function resolveHistoryBoxLabel(input: {
  boxId: string;
  box: { id: string; name: string };
  childBoxes: BoxListItem[];
}): string {
  const trimmedId = input.boxId.trim();
  if (!trimmedId) {
    return input.box.name || "Текущая коробка";
  }

  if (trimmedId === input.box.id) {
    return input.box.name || trimmedId;
  }

  const fromChild = input.childBoxes.find((item) => item.id === trimmedId);
  if (fromChild?.name?.trim()) {
    return fromChild.name.trim();
  }

  return `Коробка ${shortenId(trimmedId)}`;
}

export function buildBoxHistoryItems(
  input: BuildHistoryItemsInput,
): BoxHistoryItemView[] {
  return input.entries.map((entry) => {
    const boxId = (entry.fromTo || "").trim() || input.box.id;
    return {
      id: entry.id,
      title: getBoxHistoryProductJournalActionTitle(entry.action),
      tone: getBoxHistoryProductJournalActionTone(entry.action),
      timestamp: formatDate(entry.date),
      actorType: entry.actorType,
      actorLabel: entry.actorLabel,
      productId: entry.productId,
      productLabel: resolveHistoryProductLabel({
        productId: entry.productId,
        historyProductNamesById: input.historyProductNamesById,
        childProducts: input.childProducts,
      }),
      productHint: shortenId(entry.productId),
      boxId,
      quantityLabel: formatQuantity(entry.value),
      boxLabel: resolveHistoryBoxLabel({
        boxId,
        box: input.box,
        childBoxes: input.childBoxes,
      }),
      boxHint: shortenId(boxId),
      comment: entry.comment?.trim() || null,
    };
  });
}

export async function resolveHistoryProductNames(input: {
  api: IWarehouseApi;
  entries: ProductJournalEntry[];
  childProducts: BoxProductItem[];
}): Promise<Record<string, string>> {
  const namesMap: Record<string, string> = {};

  for (const product of input.childProducts) {
    const productId = product.id.trim();
    const productName = product.name.trim();
    if (productId && productName) {
      namesMap[productId] = productName;
    }
  }

  const idsToFetch = Array.from(
    new Set(
      input.entries
        .map((entry) => entry.productId.trim())
        .filter((id) => !!id && !namesMap[id]),
    ),
  );

  if (idsToFetch.length === 0) {
    return namesMap;
  }

  const responses = await Promise.allSettled(
    idsToFetch.map(async (id) => {
      const product = await input.api.getProductById(id);
      return { id, name: product?.name?.trim() || "" };
    }),
  );

  responses.forEach((result) => {
    if (result.status !== "fulfilled") {
      return;
    }

    if (result.value.name) {
      namesMap[result.value.id] = result.value.name;
    }
  });

  return namesMap;
}
